import React, { memo, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDrag, useDrop } from 'react-dnd';
import { ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus } from '@fortawesome/free-solid-svg-icons';
import itemTypes from '../itemTypes';

function SelectionItem({
  name, label, onClick, active, onDblClick, onHover,
  padded, errored,
}) {
  const ref = useRef(null);
  const [{ isOver }, drop] = useDrop({
    accept: [itemTypes.availableField, itemTypes.selection],
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      isOverCurrent: monitor.isOver({ shallow: true }),
    }),
    hover: (item, monitor) => {
      if (ref.current) {
        const gbcr = ref.current.getBoundingClientRect();
        const cursor = monitor.getClientOffset();
        if ((gbcr.top + gbcr.height / 2) > cursor.y) {
          onHover(name);
        }
      }
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: itemTypes.selection,
    item: { name, type: itemTypes.selection },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const dragProps = useMemo(() => ({
    opacity: isDragging ? 0.4 : 1,
  }), [isDragging]);

  const btnProps = useMemo(() => ({
    pt: padded ? 3 : 0.5,
    transition: 'padding 0.3s ease-out',
    bgcolor: isOver ? 'grey.100' : null,
  }), [isOver, padded]);

  drop(ref);

  return (
    <ListItemButton
      sx={btnProps}
      selected={active}
      onClick={() => onClick(name)}
      onDoubleClick={() => onDblClick(name)}
      ref={ref}
      data-name={name}
    >
      <ListItemIcon sx={{
        minWidth: 30, width: 30,
      }}
      >
        <FontAwesomeIcon
          icon={faMinus}
        />
      </ListItemIcon>

      <ListItemText color={errored ? 'error' : 'text.primary'} variant="body2" ref={drag} sx={dragProps}>
        {label}
      </ListItemText>
    </ListItemButton>
  );
}

SelectionItem.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  onDblClick: PropTypes.func.isRequired,
  active: PropTypes.bool,
  onHover: PropTypes.func.isRequired,
  padded: PropTypes.bool,
  errored: PropTypes.bool,
};

SelectionItem.defaultProps = {
  label: '???',
  active: false,
  padded: false,
  errored: false,
};

export default memo(SelectionItem);
